import { useEffect, useState } from 'react';
import type { OperatorTaskDetailsResponse } from '@/core/generated/api';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import type { OperatorTaskOrigin } from '@/entities/OperatorTask/model/types';
import { RequestStatus } from '@/shared/model/requestStatus';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { discardUnsavedChanges } from '@/shared/model/unsavedChanges';
import { OPERATOR_TASK_FORM_GROUP } from '../model/constants';
import { SidePanel } from '@/shared/ui/SidePanel/SidePanel';
import {
    RequestEmpty,
    RequestError,
    RequestErrorBanner,
    RequestLoading,
} from '@/shared/ui/RequestState';
import { useTaskCompletion } from '../model/useTaskCompletion';
import { OperatorTaskForm } from './OperatorTaskForm';
import { OperatorTaskDetails } from './OperatorTaskDetails';

interface OperatorTaskPanelProps {
    taskId?: string | null;
    creating?: boolean;
    editing?: boolean;
    origin?: OperatorTaskOrigin;
    sourceUnavailable?: boolean;
    onClose: () => void;
    onOpen: (id: string, editing?: boolean) => void;
    onChanged?: () => void;
}

/** Открывает создание, редактирование и историю задачи */
export const OperatorTaskPanel = (
    { taskId, creating = false, editing = false, origin, sourceUnavailable, onClose, onOpen, onChanged }: OperatorTaskPanelProps
) => {
    const current = useOperatorTasks((state) => state.current);
    const detailsStatus = useOperatorTasks((state) => state.detailsStatus);
    const detailsError = useOperatorTasks((state) => state.detailsError);
    const options = useOperatorTasks((state) => state.options);
    const optionsStatus = useOperatorTasks((state) => state.optionsStatus);
    const optionsError = useOperatorTasks((state) => state.optionsError);
    const offset = useOperatorTasks((state) => state.clockOffset);
    const fetchDetails = useOperatorTasks((state) => state.fetchDetails);
    const fetchHistory = useOperatorTasks((state) => state.fetchHistory);
    const fetchOptions = useOperatorTasks((state) => state.fetchOptions);
    const clearCurrent = useOperatorTasks((state) => state.clearCurrent);
    const canCreate = useCanAccessPermission('admin.operator-tasks.create');
    const canEdit = useCanAccessPermission('admin.operator-tasks.edit');
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);
    const completion = useTaskCompletion(taskId ?? 'new');
    const visible = Boolean(taskId || creating);

    useEffect(() => {
        if (visible && useOperatorTasks.getState().optionsStatus === RequestStatus.Idle) void fetchOptions();
    }, [visible, fetchOptions]);

    useEffect(() => {
        if (!taskId) return;

        void fetchDetails(taskId);
        void fetchHistory(taskId);

        return clearCurrent;
    }, [taskId, fetchDetails, fetchHistory, clearCurrent]);

    /** Закрывает задачу после решения оператора о введённых правках */
    const close = () => {
        if (busy || completion.pending.length) return;

        confirmUnsavedChanges(dirty, () => {
            discardUnsavedChanges(OPERATOR_TASK_FORM_GROUP);
            onClose();
        });
    };

    /** Показывает сохранённую задачу */
    const saved = (task: OperatorTaskDetailsResponse) => {
        onChanged?.();

        if (!task.id) {
            onClose();

            return;
        }

        onOpen(task.id);

        if (task.id === taskId) {
            void fetchDetails(task.id);
            void fetchHistory(task.id);
        }
    };

    return (
        <SidePanel
            visible={visible}
            title={creating
                ? 'Новая задача'
                : editing
                    ? `Редактирование задачи № ${current?.taskNumber ?? '…'}`
                    : `Задача № ${current?.taskNumber ?? '…'}`
            }
            onHide={close}
            closable={!busy && completion.pending.length === 0}
        >
            {(creating || editing) && optionsStatus === RequestStatus.Loading &&
                <RequestLoading />
            }
            {(creating || editing) &&
                <RequestErrorBanner
                    status={optionsStatus}
                    error={optionsError}
                    fallbackMessage="Настройки формы недоступны"
                    onRetry={fetchOptions}
                />
            }
            {((creating && !canCreate) || (editing && !canEdit)) &&
                <RequestError message="Нет права на изменение задачи" />
            }
            {taskId && detailsStatus === RequestStatus.Loading &&
                <RequestLoading />
            }
            {taskId &&
                <RequestErrorBanner
                    status={detailsStatus}
                    error={detailsError}
                    fallbackMessage="Задача недоступна"
                    onRetry={() => fetchDetails(taskId)}
                />
            }
            {taskId && detailsStatus === RequestStatus.Success && current?.id !== taskId &&
                <RequestEmpty message="Задача не найдена" />
            }
            {options && ((creating && canCreate) || (editing && canEdit && current && current.id === taskId && current.status?.id === 'inProgress')) && (
                <OperatorTaskForm
                    key={`${taskId ?? 'new'}-${editing}`}
                    options={options}
                    task={creating
                        ? undefined
                        : current ?? undefined
                    }
                    origin={creating
                        ? origin
                        : undefined
                    }
                    sourceUnavailable={creating && sourceUnavailable}
                    clockOffset={offset}
                    onClose={onClose}
                    onBusyChange={setBusy}
                    onDirtyChange={setDirty}
                    onSaved={saved}
                    onReload={() => {
                        if (taskId) {
                            onOpen(taskId);
                            void fetchDetails(taskId);
                            void fetchHistory(taskId);
                        }
                    }}
                />
            )}
            {current && current.id === taskId && (!editing || current.status?.id === 'completed') && !creating && (
                <OperatorTaskDetails
                    task={current}
                    onEdit={() => { if (taskId) onOpen(taskId, true); }}
                    busy={Boolean(taskId && completion.pending.includes(taskId))}
                    onStatusChange={(reopen) => { void completion.changeStatus(current, reopen).then(onChanged); }}
                />
            )}
            {completion.error &&
                <RequestError
                    message={completion.error}
                    compact
                />
            }
        </SidePanel>
    );
};
